/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { LanguageSelector } from '../../../../../editor/common/languageSelector.js';
import { localize } from '../../../../../nls.js';

/**
 * Documentation link for the reusable prompts feature.
 */
const PROMPT_DOCUMENTATION_URL = 'https://aka.ms/vscode-ghcp-prompt-snippets';
const INSTRUCTIONS_DOCUMENTATION_URL = 'https://aka.ms/vscode-ghcp-custom-instructions';
const AGENT_DOCUMENTATION_URL = 'https://aka.ms/vscode-ghcp-custom-chat-modes'; // todo
const SKILL_DOCUMENTATION_URL = 'https://aka.ms/vscode-agent-skills';
// TODO: update link when available
const HOOK_DOCUMENTATION_URL = 'https://aka.ms/vscode-chat-hooks';

export function getDocumentationUrl(type: PromptsType): string {
	switch (type) {
		case PromptsType.prompt:
			return PROMPT_DOCUMENTATION_URL;
		case PromptsType.instructions:
			return INSTRUCTIONS_DOCUMENTATION_URL;
		case PromptsType.agent:
			return AGENT_DOCUMENTATION_URL;
		case PromptsType.skill:
			return SKILL_DOCUMENTATION_URL;
		case PromptsType.hook:
			return HOOK_DOCUMENTATION_URL;
		default:
			throw new Error(`Unknown prompt type: ${type}`);
	}
}

/**
 * Language ID for the reusable prompt syntax.
 */
export const PROMPT_LANGUAGE_ID = 'prompt';

/**
 * Language ID for instructions syntax.
 */
export const INSTRUCTIONS_LANGUAGE_ID = 'instructions';

/**
 * Language ID for agent syntax.
 */
export const AGENT_LANGUAGE_ID = 'chatagent';

/**
 * Language ID for skill syntax.
 */
export const SKILL_LANGUAGE_ID = 'skill';

/**
 * Prompt and instructions files language selector.
 */
export const ALL_PROMPTS_LANGUAGE_SELECTOR: LanguageSelector = [PROMPT_LANGUAGE_ID, INSTRUCTIONS_LANGUAGE_ID, AGENT_LANGUAGE_ID, SKILL_LANGUAGE_ID];

/**
 * Configuration key for enabling file logging for the agent debug log.
 */
export const AGENT_DEBUG_LOG_FILE_LOGGING_ENABLED_SETTING = 'github.copilot.chat.agentDebugLog.fileLogging.enabled';

/**
 * Configuration key for enabling agent debug logging for agent host (Copilot CLI) sessions.
 * Registered in core (see `chat.shared.contribution.ts`) since only core consumes it.
 */
export const AgentHostAgentDebugLogEnabledSettingId = 'chat.agentHost.agentDebugLog.enabled';

/**
 * Configuration key for the maximum number of debug events kept in memory for
 * agent host (Copilot CLI) sessions. Registered in core (see
 * `chat.shared.contribution.ts`) since only core consumes it.
 */
export const AgentHostAgentDebugLogMaxEventsSettingId = 'chat.agentHost.agentDebugLog.maxEventsInMemory';

/**
 * The name of the troubleshoot slash command / skill.
 */
export const TROUBLESHOOT_COMMAND_NAME = 'troubleshoot';

/**
 * URI scheme used by the Copilot extension for built-in skills.
 */
export const COPILOT_SKILL_URI_SCHEME = 'copilot-skill';

/**
 * Path fragment that identifies the troubleshoot skill in a URI.
 */
export const TROUBLESHOOT_SKILL_PATH = 'troubleshoot/SKILL.md';

/**
 * The language id for a prompts type.
 */
export function getLanguageIdForPromptsType(type: PromptsType): string {
	switch (type) {
		case PromptsType.prompt:
			return PROMPT_LANGUAGE_ID;
		case PromptsType.instructions:
			return INSTRUCTIONS_LANGUAGE_ID;
		case PromptsType.agent:
			return AGENT_LANGUAGE_ID;
		case PromptsType.skill:
			return SKILL_LANGUAGE_ID;
		case PromptsType.hook:
			// Hooks use JSONC syntax with schema validation
			return 'jsonc';
		default:
			throw new Error(`Unknown prompt type: ${type}`);
	}
}

export function getPromptsTypeForLanguageId(languageId: string): PromptsType | undefined {
	switch (languageId) {
		case PROMPT_LANGUAGE_ID:
			return PromptsType.prompt;
		case INSTRUCTIONS_LANGUAGE_ID:
			return PromptsType.instructions;
		case AGENT_LANGUAGE_ID:
			return PromptsType.agent;
		case SKILL_LANGUAGE_ID:
			return PromptsType.skill;
		// Note: hook uses 'jsonc' language ID which is shared, so we don't map it here
		default:
			return undefined;
	}
}


/**
 * What the prompt is used for.
 */
export enum PromptsType {
	instructions = 'instructions',
	prompt = 'prompt',
	agent = 'agent',
	skill = 'skill',
	hook = 'hook'
}
export function isValidPromptType(type: string): type is PromptsType {
	return Object.values(PromptsType).includes(type as PromptsType);
}

export enum Target {
	VSCode = 'vscode',
	GitHubCopilot = 'github-copilot',
	Claude = 'claude',
	Undefined = 'undefined',
}

/**
 * Tracks where prompt files originate from.
 */
export enum PromptFileSource {
	GitHubWorkspace = 'github-workspace',
	CopilotPersonal = 'copilot-personal',
	ClaudePersonal = 'claude-personal',
	ClaudeWorkspace = 'claude-workspace',
	ClaudeWorkspaceLocal = 'claude-workspace-local',
	AgentsWorkspace = 'agents-workspace',
	AgentsPersonal = 'agents-personal',
	ConfigWorkspace = 'config-workspace',
	ConfigPersonal = 'config-personal',
	UserData = 'user-data',
	ExtensionContribution = 'extension-contribution',
	ExtensionAPI = 'extension-api',
	Plugin = 'plugin',
}

/**
 * Returns a human-readable description for a prompt file source.
 */
export function getSourceDescription(source: PromptFileSource): string | undefined {
	switch (source) {
		case PromptFileSource.AgentsWorkspace:
			return localize('source.agentsWorkspace', "Workspace");
		case PromptFileSource.AgentsPersonal:
			return localize('source.agentsPersonal', "Global");
		case PromptFileSource.GitHubWorkspace:
			return localize('source.githubWorkspace', "Workspace (only used by Copilot agents)");
		case PromptFileSource.CopilotPersonal:
			return localize('source.copilotPersonal', "Global (only used by Copilot agents)");
		case PromptFileSource.ClaudeWorkspace:
			return localize('source.claudeWorkspace', "Workspace (only used by Claude agents)");
		case PromptFileSource.ClaudeWorkspaceLocal:
			return localize('source.claudeWorkspaceLocal', "Workspace (only used by Claude agents, usually git-ignored)");
		case PromptFileSource.ClaudePersonal:
			return localize('source.claudePersonal', "Global (only used by Claude agents)");
		case PromptFileSource.UserData:
			return localize('source.userData', "Global (roams with Settings Sync, only used by VS Code)");
		case PromptFileSource.ConfigWorkspace:
			return localize('source.configWorkspace', "Workspace (contributed from settings)");
		case PromptFileSource.ConfigPersonal:
			return localize('source.configPersonal', "Global (contributed from settings)");
		default:
			return undefined;
	}
}
